import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Modal, VideoPlayer, ImagePair, Thumb, Stepper } from '../components/ui';
import { estimate1RM, warmupSets, suggestProgression } from '../lib/calc';
import { notifyRestDone, requestNotificationPermission } from '../lib/restNotify';
import { PlateModal } from '../components/PlateCalc';
import { celebrateRecord, celebrateStrengthLevel, enqueueCelebration } from '../lib/fireworks';
import { playSound, unlockAudio, isSoundEnabled, setSoundEnabled } from '../lib/sounds';

// Variante (principale ou alternative) qui possède déjà des séries enregistrées pour cette séance
function pickActiveVariant(mainExercise, existingSetsByExercise) {
  const variants = [mainExercise, ...(mainExercise.alternatives || [])];
  // Une série "vide" (poids et reps nuls) ne compte pas : sinon un enregistrement
  // accidentel sur le principal masquerait des vraies données saisies sur l'alternatif
  const hasRealData = (sets) => (sets || []).some((s) => s.weight != null || s.reps != null);
  const withData = variants.find((variant) => hasRealData(existingSetsByExercise[variant.id]));
  return (withData || mainExercise).id;
}

// Au moins `min` séries avec des données réelles (poids ou reps renseignés)
export function isSetsDone(sets, min) {
  return (sets || []).filter((s) => s.weight != null || s.reps != null).length >= min;
}

const SEEN_BADGES_KEY = 'muscuapp_seen_badges';
const SEEN_STRENGTH_KEY = 'muscuapp_seen_strength';

function formatRest(seconds) {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s}s`;
}

function SetRow({ setNum, value, onChange, prevValue, isTimed, bestWeight = 0, onRecord, exerciseName }) {
  const [plates, setPlates] = useState(false);
  const repsLabel = isTimed ? 'Secondes' : 'Reps';
  const repsPlaceholder = isTimed ? 'sec' : 'reps';
  const isPR = bestWeight > 0 && value.weight > 0 && value.weight > bestWeight;
  useEffect(() => {
    if (!isPR || !onRecord) return undefined;
    // Attend que la charge soit stabilisée avant de célébrer (les boutons +/- enchaînent les valeurs)
    const id = setTimeout(() => onRecord(value.weight), 1200);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [isPR, value.weight]);
  return (
    <div className="set-row">
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="set-num">{setNum}</div>
      </div>
      <div>
        <Stepper
          decimal
          step={2.5}
          ariaLabel="Poids (kg)"
          value={value.weight}
          base={prevValue?.weight}
          placeholder={prevValue?.weight != null ? String(prevValue.weight) : 'kg'}
          onChange={(weight) => onChange({ ...value, weight })}
        />
        <div className="set-input-label">
          Poids (kg)
          {!isTimed && value.weight > 0 && (
            <button type="button" className="plate-btn" onClick={() => setPlates(true)} title="Calculateur de disques" aria-label="Calculateur de disques">🛞</button>
          )}
          {isPR && (
            <span style={{ color: '#f59e0b', marginLeft: 6, fontWeight: 700, fontSize: 11 }}>🏆 PR !</span>
          )}
          {prevValue?.weight != null && (
            <span style={{ color: 'var(--accent2)', marginLeft: 4, fontWeight: 600 }}>← {prevValue.weight}</span>
          )}
        </div>
      </div>
      <div>
        <Stepper
          step={isTimed ? 5 : 1}
          ariaLabel={repsLabel}
          value={value.reps}
          base={prevValue?.reps}
          placeholder={prevValue?.reps != null ? String(prevValue.reps) : repsPlaceholder}
          onChange={(reps) => onChange({ ...value, reps })}
        />
        <div className="set-input-label">
          {repsLabel}{!isTimed && estimate1RM(value.weight, value.reps) != null && value.reps > 1 && (
            <span style={{ color: 'var(--text2)', marginLeft: 4 }}>· 1RM ≈ {Math.round(estimate1RM(value.weight, value.reps))}</span>
          )}{prevValue?.reps != null && (
            <span style={{ color: 'var(--accent2)', marginLeft: 4, fontWeight: 600 }}>← {prevValue.reps}</span>
          )}
        </div>
      </div>
      {plates && <PlateModal weight={value.weight} reps={value.reps} exerciseName={exerciseName} onClose={() => setPlates(false)} />}
    </div>
  );
}

function RestTimer({ endTime, onAdjust, onDismiss }) {
  const remainingOf = () => Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
  const [timeLeft, setTimeLeft] = useState(remainingOf);
  const alerted = useRef(false);

  useEffect(() => {
    alerted.current = false;
    const tick = () => {
      const remaining = remainingOf();
      setTimeLeft(remaining);
      if (remaining <= 0 && !alerted.current) {
        alerted.current = true;
        if (navigator.vibrate) navigator.vibrate([400, 150, 400, 150, 400]);
        playSound('timerEnd');
        notifyRestDone();
      }
    };
    tick();
    // Le temps est calculé sur l'heure de fin : il reste exact après une mise en veille de l'écran
    const id = setInterval(tick, 500);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
    // eslint-disable-next-line
  }, [endTime]);

  const done = timeLeft <= 0;
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const display = `${mins}:${String(secs).padStart(2, '0')}`;

  return (
    <div className={`rest-timer${done ? ' rest-timer--done' : ''}`} role="timer" aria-live="polite">
      <div>
        <div className="rest-timer-label">{done ? '✅ Repos terminé !' : '⏱ Temps de repos'}</div>
        {!done && <div className="rest-timer-time">{display}</div>}
      </div>
      <div className="rest-timer-actions">
        {!done && <button type="button" onClick={() => onAdjust(-15)}>−15s</button>}
        <button type="button" onClick={() => onAdjust(done ? 60 : 15)}>{done ? '+1 min' : '+15s'}</button>
        <button type="button" className="rest-timer-ok" onClick={onDismiss}>{done ? 'OK' : 'Ignorer'}</button>
      </div>
    </div>
  );
}

// Liste des exercices de la bibliothèque du même groupe musculaire (machine occupée → alternative)
function AlternativePicker({ mainExercise, currentLibraryId, onPick, onClose }) {
  const category = mainExercise.lib_category;
  const { data: library = [], isLoading } = useQuery({ queryKey: ['library'], queryFn: api.getLibrary });
  const [search, setSearch] = useState('');
  const [equipment, setEquipment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sameMuscle = useMemo(
    () => library.filter((item) => !category || item.category === category),
    [library, category]
  );
  const equipments = useMemo(
    () => [...new Set(sameMuscle.map((item) => item.equipment).filter(Boolean))].sort(),
    [sameMuscle]
  );
  const query = search.trim().toLowerCase();
  const items = sameMuscle.filter((item) =>
    (!equipment || item.equipment === equipment) && (!query || item.name.toLowerCase().includes(query))
  );

  const pick = async (libraryId) => {
    setBusy(true);
    setError('');
    try {
      await onPick(libraryId);
    } catch (err) {
      setError(err.message || 'Impossible de changer d\'exercice');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Alternative${category ? ` — ${category}` : ''}`} onClose={onClose} wide>
      <p className="muted small" style={{ marginTop: 0 }}>
        Exercice prévu : <strong>{mainExercise.name}</strong>. Choisis un autre exercice du même groupe musculaire pour cette séance.
      </p>
      {currentLibraryId !== mainExercise.library_id && (
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => pick(mainExercise.library_id)} style={{ marginBottom: 10 }}>
          ↩ Revenir à l'exercice prévu
        </button>
      )}
      <div className="alt-filters">
        <input
          className="form-input"
          type="search"
          placeholder="Rechercher un exercice…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select" aria-label="Matériel" value={equipment} onChange={(e) => setEquipment(e.target.value)}>
          <option value="">Tout le matériel</option>
          {equipments.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>
      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}
      {isLoading ? (
        <div className="loading"><div className="spinner" />Chargement...</div>
      ) : items.length === 0 ? (
        <p className="muted">Aucun exercice trouvé.</p>
      ) : (
        <div className="alt-list">
          {items.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`alt-item${item.id === currentLibraryId ? ' alt-item--current' : ''}`}
              disabled={busy}
              onClick={() => pick(item.id)}
            >
              <Thumb src={item.image_url} alt={item.name} size={48} />
              <span className="alt-item-text">
                <span className="alt-item-name">{item.name}</span>
                <span className="muted small">{item.equipment || '—'}{item.id === mainExercise.library_id ? ' · prévu' : ''}{item.id === currentLibraryId ? ' · en cours' : ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function FreeExercisePicker({ existingIds, onPick, onClose }) {
  const { data: library = [], isLoading, error: loadError } = useQuery({ queryKey: ['library'], queryFn: api.getLibrary });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const categories = [...new Set(library.map((item) => item.category).filter(Boolean))].sort();
  const query = search.trim().toLowerCase();
  const items = library.filter((item) =>
    (!category || item.category === category) && (!query || item.name.toLowerCase().includes(query))
  );

  const pick = async (libraryId) => {
    setBusy(true);
    setError('');
    try {
      await onPick(libraryId);
      onClose();
    } catch (err) {
      setError(err.message || "Impossible d'ajouter cet exercice");
      setBusy(false);
    }
  };

  return (
    <Modal title="Ajouter un exercice" onClose={onClose} wide>
      <div className="alt-filters">
        <input
          className="form-input"
          type="search"
          placeholder="Rechercher un exercice…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select" aria-label="Groupe musculaire" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Tous les groupes</option>
          {categories.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>
      {(error || loadError) && <p style={{ color: 'var(--danger)' }}>{error || loadError.message}</p>}
      {isLoading ? (
        <div className="loading"><div className="spinner" />Chargement...</div>
      ) : items.length === 0 ? (
        <p className="muted">Aucun exercice trouvé.</p>
      ) : (
        <div className="alt-list">
          {items.map((item) => (
            <button
              type="button"
              key={item.id}
              className="alt-item"
              disabled={busy || existingIds.has(item.id)}
              onClick={() => pick(item.id)}
            >
              <Thumb src={item.image_url} alt={item.name} size={48} />
              <span className="alt-item-text">
                <span className="alt-item-name">{item.name}</span>
                <span className="muted small">{item.category} · {item.equipment || '—'}{existingIds.has(item.id) ? ' · déjà ajouté' : ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function ExerciseBlock({
  mainExercise, sessionId, existingSetsByExercise, onSave, onRestStart, onSkip, onRemove, onMove, canMoveUp, canMoveDown,
  isFreeWorkout = false, prevSets, lastPerf, bestWeight = 0,
}) {
  const celebrated = useRef(0);
  const queryClient = useQueryClient();
  const variants = [mainExercise, ...(mainExercise.alternatives || [])];
  const [activeExerciseId, setActiveExerciseId] = useState(mainExercise.id);
  const userInteracted = useRef(false);
  const autoSelectDone = useRef(false);

  // Reprise de séance (ex: réveil du téléphone) : resélectionne la variante déjà remplie
  useEffect(() => {
    if (autoSelectDone.current || userInteracted.current) return;
    if (Object.keys(existingSetsByExercise).length === 0) return;
    const target = pickActiveVariant(mainExercise, existingSetsByExercise);
    if (target !== activeExerciseId) setActiveExerciseId(target);
    autoSelectDone.current = true;
    // eslint-disable-next-line
  }, [existingSetsByExercise, mainExercise]);

  const exercise = variants.find((variant) => variant.id === activeExerciseId) || mainExercise;
  const currentExistingSets = existingSetsByExercise[exercise.id] || [];
  const existingSetsKey = currentExistingSets
    .map((set) => `${set.id}:${set.set_number}:${set.weight ?? ''}:${set.reps ?? ''}:${set.notes ?? ''}`)
    .join('|');
  const getPrevSet = (setNum) => {
    // Priorité : lastPerf (perf par exercice individuel)
    const perfEntry = lastPerf?.[exercise.id];
    if (perfEntry) return perfEntry.sets.find((s) => s.set_number === setNum);
    // Fallback : ancienne logique session précédente
    return prevSets?.find((s) => s.exercise_id === exercise.id && s.set_number === setNum);
  };

  const targetSets = isFreeWorkout
    ? Math.max(currentExistingSets.length, 1)
    : exercise.target_sets || 3;
  const suggestion = suggestProgression(lastPerf?.[exercise.id]?.sets, {
    repsMax: exercise.target_reps_max,
    isTimed: !!exercise.is_timed,
  });
  const initSets = () => {
    const base = Array.from({ length: targetSets }, (_, i) => ({
      set_number: i + 1,
      weight: null,
      reps: null,
      notes: null,
    }));
    if (currentExistingSets && currentExistingSets.length > 0) {
      return base.map((s) => {
        const found = currentExistingSets.find((es) => es.set_number === s.set_number);
        return found ? { set_number: s.set_number, weight: found.weight, reps: found.reps, notes: found.notes, id: found.id } : s;
      });
    }
    return base;
  };

  const applyPrevious = (progress) => {
    setSets((current) => current.map((s) => {
      const prev = getPrevSet(s.set_number) || getPrevSet(1);
      if (!prev) return s;
      const empty = s.weight == null && s.reps == null;
      if (!empty) return s;
      return { ...s, weight: progress && suggestion ? suggestion.weight : prev.weight, reps: progress ? exercise.target_reps_min ?? prev.reps : prev.reps };
    }));
  };

  const handleDeleteSet = async () => {
    const last = sets[sets.length - 1];
    setSets((prev) => prev.slice(0, -1));
    setSaved(false);
    if (last?.id && sessionId) {
      try { await api.deleteSet(sessionId, last.id); } catch (_) {}
    }
  };

  const [sets, setSets] = useState(initSets);
  const [open, setOpen] = useState(true);
  const [saved, setSaved] = useState(false);
  const [panel, setPanel] = useState(null);
  const [workWeight, setWorkWeight] = useState('');

  useEffect(() => {
    setSets(initSets());
    setSaved(false);
    // eslint-disable-next-line
  }, [exercise.id, existingSetsKey]);

  const handleSave = async () => {
    const payload = sets.map((s) => ({
      exercise_id: exercise.id,
      set_number: s.set_number,
      reps: s.reps,
      weight: s.weight,
      notes: s.notes,
    }));
    await onSave(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    if (exercise.rest_seconds && onRestStart && !isSetsDone(sets, isFreeWorkout ? sets.length : targetSets)) {
      onRestStart(exercise.rest_seconds);
    }
  };

  const chooseExercise = async (libraryId) => {
    userInteracted.current = true;
    if (libraryId === mainExercise.library_id) {
      setActiveExerciseId(mainExercise.id);
    } else {
      const chosen = await api.substituteExercise(mainExercise.id, libraryId);
      await queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setActiveExerciseId(chosen.id);
    }
    setSaved(false);
    setPanel(null);
  };

  const canSkip = !!(onSkip && sessionId) && !variants.some((variant) => isSetsDone(existingSetsByExercise[variant.id], 1));

  const unit = exercise.is_timed ? 'sec' : 'reps';
  const repsLabel = exercise.target_reps_min && exercise.target_reps_max
    ? exercise.target_reps_min === exercise.target_reps_max
      ? `${exercise.target_reps_min} ${unit}`
      : `${exercise.target_reps_min}-${exercise.target_reps_max} ${unit}`
    : exercise.notes || "Jusqu'à l'échec";

  return (
    <div className={`exercise-block${saved ? ' exercise-block--saved' : ''}`}>
      <div className="exercise-block-header" onClick={() => setOpen((o) => !o)}>
        <button
          type="button"
          className="thumb-btn"
          aria-label={`Voir la technique : ${exercise.name}`}
          onClick={(e) => { e.stopPropagation(); setPanel('info'); }}
        >
          <Thumb src={exercise.image_url} alt={exercise.name} size={64} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="exercise-name">{exercise.name}</div>
            {exercise.id !== mainExercise.id && (
              <span style={{ fontSize: 11, background: 'var(--warning)', color: '#000', borderRadius: 4, padding: '1px 6px', fontWeight: 700, flexShrink: 0 }}>ALT</span>
            )}
          </div>
          <div className="exercise-meta">
            {isFreeWorkout ? sets.length : targetSets} séries × {repsLabel}{exercise.notes && !exercise.is_timed ? ` — ${exercise.notes}` : ''}
            {formatRest(exercise.rest_seconds) && (
              <span style={{ marginLeft: 8, color: 'var(--accent2)', fontWeight: 600 }}>
                ⏱ {formatRest(exercise.rest_seconds)}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span className={`chevron${open ? ' open' : ''}`}>▼</span>
        </div>
      </div>
      {open && (
        <div className="exercise-block-body">
          <div className="ex-tools">
            <button className="btn btn-ghost btn-sm" onClick={() => setPanel('info')}>ℹ️ Technique</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setPanel('warmup')}>🔺 Échauffement</button>
            {!isFreeWorkout && <button className="btn btn-ghost btn-sm" onClick={() => setPanel('alternative')}>🔁 Alternative</button>}
            {isFreeWorkout && (
              <>
                <button type="button" className="btn btn-ghost btn-sm" disabled={!canMoveUp} onClick={() => onMove(mainExercise.id, -1)}>↑ Monter</button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={!canMoveDown} onClick={() => onMove(mainExercise.id, 1)}>↓ Descendre</button>
                <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => onRemove(mainExercise)}>🗑 Retirer</button>
              </>
            )}
            {canSkip && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--danger)' }}
                onClick={() => { if (window.confirm(`Retirer « ${mainExercise.name} » de cette séance ?`)) onSkip(mainExercise.id); }}
              >
                🗑 Retirer de la séance
              </button>
            )}
            {getPrevSet(1) && <button className="btn btn-ghost btn-sm" onClick={() => applyPrevious(false)}>↺ Comme la dernière fois</button>}
          </div>
          {suggestion && (
            <div className="progress-hint">
              <span>💡 <strong>{suggestion.weight} kg</strong> <span className="muted small">{suggestion.reason}</span></span>
              <button className="btn btn-sm btn-ghost" onClick={() => applyPrevious(true)}>Appliquer</button>
            </div>
          )}
          <div className="sets-grid">
            {sets.map((s, i) => (
              <SetRow
                key={i}
                setNum={s.set_number}
                value={s}
                prevValue={getPrevSet(s.set_number)}
                isTimed={!!exercise.is_timed}
                bestWeight={bestWeight}
                exerciseName={exercise.name}
                onRecord={(weight) => {
                  if (weight > celebrated.current) {
                    celebrated.current = weight;
                    celebrateRecord(`${exercise.name} — ${weight} kg`);
                  }
                }}
                onChange={(updated) => {
                  const newSets = [...sets];
                  newSets[i] = updated;
                  setSets(newSets);
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {sets.length > 1 && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--danger)', borderColor: 'var(--danger)', opacity: 0.75 }}
                  onClick={handleDeleteSet}
                >
                  − Série
                </button>
              )}
              {isFreeWorkout && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setSets((current) => [...current, {
                      set_number: current.length + 1,
                      weight: null,
                      reps: null,
                      notes: null,
                    }]);
                    setSaved(false);
                  }}
                >
                  + Série
                </button>
              )}
            </div>
            <button
              className={`btn ${saved ? 'btn-success' : 'btn-primary'} btn-sm`}
              onClick={handleSave}
              disabled={!sessionId}
            >
              {saved ? '✓ Enregistré !' : '💾 Enregistrer'}
            </button>
          </div>
        </div>
      )}
      {panel === 'alternative' && (
        <AlternativePicker
          mainExercise={mainExercise}
          currentLibraryId={exercise.library_id}
          onPick={chooseExercise}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'info' && (
        <Modal title={exercise.name} onClose={() => setPanel(null)} wide>
          <ImagePair start={exercise.image_url} end={exercise.image_url_end} alt={exercise.name} />
          <VideoPlayer url={exercise.video_url} />
          {exercise.lib_description && <p>{exercise.lib_description}</p>}
          {exercise.lib_tips && <p className="tip-box">💡 {exercise.lib_tips}</p>}
          {!exercise.image_url && !exercise.video_url && !exercise.lib_description && (
            <p className="muted">Aucun média. Ajoute-en depuis la bibliothèque d'exercices.</p>
          )}
        </Modal>
      )}
      {panel === 'warmup' && (
        <Modal title={`Échauffement — ${exercise.name}`} onClose={() => setPanel(null)}>
          <div className="form-group">
            <label className="form-label">Charge de travail (kg)</label>
            <input
              type="number"
              className="form-input"
              min="0"
              step="0.5"
              autoFocus
              value={workWeight}
              placeholder={String(sets.find((s) => s.weight)?.weight ?? getPrevSet(1)?.weight ?? bestWeight ?? '')}
              onChange={(e) => setWorkWeight(e.target.value)}
            />
          </div>
          {(() => {
            const base = parseFloat(workWeight) || sets.find((s) => s.weight)?.weight || getPrevSet(1)?.weight || bestWeight;
            const list = warmupSets(base);
            return list.length ? (
              <ol className="warmup-list">
                {list.map((w, i) => (
                  <li key={i}><span className="set-num">{i + 1}</span><strong>{w.weight} kg</strong> × {w.reps}<span className="muted small">{w.label}</span></li>
                ))}
                <li className="warmup-work"><span className="set-num">🎯</span><strong>{base} kg</strong> — séries de travail</li>
              </ol>
            ) : <p className="muted">Saisis une charge de travail supérieure à la barre.</p>;
          })()}
        </Modal>
      )}
    </div>
  );
}

const STORAGE_DAY_KEY = 'muscuapp_workout_day';
const STORAGE_DATE_KEY = 'muscuapp_workout_date';
const FREE_WORKOUT_MODE = '__free_workout__';
const FREE_SESSION_DAY = 'Séance libre';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function Workout() {
  const [searchParams] = useSearchParams();
  const requestedDay = searchParams.get('day');
  const urlDay = requestedDay || null;
  // Reprend le jour/la date en cours si l'app a été rechargée (OS qui tue l'onglet en arrière-plan)
  const [selectedDay, setSelectedDay] = useState(() => urlDay || localStorage.getItem(STORAGE_DAY_KEY) || '');
  const [sessionDate, setSessionDate] = useState(() => (!urlDay && localStorage.getItem(STORAGE_DATE_KEY)) || todayLocal());
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [sessionCreated, setSessionCreated] = useState(false);
  const wakeLockRef = useRef(null);

  const [restEndTime, setRestEndTime] = useState(null);
  const autostart = searchParams.get('autostart') === '1';

  const queryClient = useQueryClient();

  const { data: programs = [] } = useQuery({ queryKey: ['programs'], queryFn: api.getPrograms });
  const programNames = programs.map((p) => p.name);
  const favoriteNames = new Set(programs.filter((p) => p.favorite).map((p) => p.name));
  const isFreeWorkout = selectedDay === FREE_WORKOUT_MODE;

  // Retombe sur le premier programme si le jour mémorisé n'existe plus
  useEffect(() => {
    if (!isFreeWorkout && programs.length && !programNames.includes(selectedDay)) setSelectedDay(programNames[0]);
    // eslint-disable-next-line
  }, [programs, selectedDay, isFreeWorkout]);

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ['exercises', selectedDay],
    queryFn: () => api.getExercisesByDay(selectedDay),
    enabled: !!selectedDay && !isFreeWorkout,
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ['sessions'],
    queryFn: api.getSessions,
  });

  // Load existing session for same day+date if exists
  const existingSession = sessions.find(
    (s) => isFreeWorkout
      ? s.is_free && s.date === sessionDate
      : !s.is_free && s.day === selectedDay && s.date === sessionDate
  );

  const { data: sessionDetail, isLoading: isSessionDetailLoading } = useQuery({
    queryKey: ['session', sessionId || existingSession?.id],
    queryFn: () => api.getSession(sessionId || existingSession?.id),
    enabled: !!(sessionId || existingSession?.id),
  });

  const sessionSetsByExercise = useMemo(() => {
    const setsByExercise = {};
    for (const set of sessionDetail?.sets || []) {
      if (!setsByExercise[set.exercise_id]) setsByExercise[set.exercise_id] = [];
      setsByExercise[set.exercise_id].push(set);
    }
    return setsByExercise;
  }, [sessionDetail]);

  const currentSessionId = sessionId || existingSession?.id;
  const skippedIds = useMemo(() => new Set(sessionDetail?.skipped_exercise_ids || []), [sessionDetail]);
  const sessionExercises = isFreeWorkout ? (sessionDetail?.free_exercises || []) : exercises;
  const visibleExercises = sessionExercises.filter((ex) => !skippedIds.has(ex.id));
  const skippedExercises = isFreeWorkout ? [] : exercises.filter((ex) => skippedIds.has(ex.id));

  const completion = useMemo(() => {
    if (!currentSessionId || isFreeWorkout || !visibleExercises.length) return null;
    let sets = 0;
    let volume = 0;
    for (const ex of visibleExercises) {
      const variant = [ex, ...(ex.alternatives || [])].find((v) => isSetsDone(sessionSetsByExercise[v.id], 1));
      if (!variant || !isSetsDone(sessionSetsByExercise[variant.id], variant.target_sets || 3)) return null;
      for (const set of sessionSetsByExercise[variant.id]) {
        if (set.weight == null && set.reps == null) continue;
        sets += 1;
        volume += (set.weight || 0) * (set.reps || 0);
      }
    }
    return { exercises: visibleExercises.length, sets, volume: Math.round(volume) };
    // eslint-disable-next-line
  }, [currentSessionId, isFreeWorkout, visibleExercises, skippedIds, sessionSetsByExercise]);

  const [soundOn, setSoundOn] = useState(isSoundEnabled);
  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
    if (next) { unlockAudio(); playSound('sessionStart'); }
  };

  const [congrats, setCongrats] = useState(null);
  const congratsKey = currentSessionId ? `muscuapp_congrats_${currentSessionId}` : null;
  const prevCompleted = useRef(null);
  useEffect(() => {
    if (!sessionDetail) return;
    const done = !!completion;
    // Ne félicite que lors du passage à "terminé" pendant l'utilisation, pas à la réouverture d'une séance finie
    if (prevCompleted.current === false && done && !localStorage.getItem(congratsKey)) {
      localStorage.setItem(congratsKey, '1');
      enqueueCelebration(() => {
        setCongrats(completion);
        playSound('sessionEnd');
        if (navigator.vibrate) navigator.vibrate([80, 40, 120]);
      }, 1500);
    }
    prevCompleted.current = done;
    // eslint-disable-next-line
  }, [completion, sessionDetail]);

  useEffect(() => { prevCompleted.current = null; setCongrats(null); }, [currentSessionId]);

  const skipMutation = useMutation({
    mutationFn: ({ exerciseId, restore }) => (restore
      ? api.unskipExercise(currentSessionId, exerciseId)
      : api.skipExercise(currentSessionId, exerciseId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session', currentSessionId] }),
    onError: (e) => setError(e.message),
  });

  // Persiste le jour/la date choisis pour pouvoir reprendre la séance après un rechargement
  useEffect(() => {
    localStorage.setItem(STORAGE_DAY_KEY, selectedDay);
  }, [selectedDay]);

  useEffect(() => {
    localStorage.setItem(STORAGE_DATE_KEY, sessionDate);
  }, [sessionDate]);

  const requestWakeLock = async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch (err) {
      console.warn('Wake Lock refusé:', err);
    }
  };

  // Empêche la mise en veille de l'écran tant qu'une séance est en cours
  useEffect(() => {
    if (!currentSessionId || !('wakeLock' in navigator)) return;
    requestWakeLock();
    // Le verrou est relâché automatiquement quand l'onglet passe en arrière-plan
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
    // eslint-disable-next-line
  }, [currentSessionId]);

  // Collecte tous les IDs d'exercices affichés (principaux + alternatives)
  const allExerciseIds = sessionExercises.flatMap((ex) => [ex.id, ...(ex.alternatives || []).map((alternative) => alternative.id)]);

  const { data: lastPerf = {} } = useQuery({
    queryKey: ['lastPerformance', allExerciseIds, currentSessionId],
    queryFn: () => api.getLastPerformance(allExerciseIds, currentSessionId),
    enabled: allExerciseIds.length > 0,
    staleTime: 60000,
  });

  const { data: bests = [] } = useQuery({
    queryKey: ['bests'],
    queryFn: api.getBests,
  });
  // Index: exercice principal → meilleur poids connu
  const bestMap = Object.fromEntries(bests.map(b => [b.exercise_id, b.max_weight || 0]));

  useEffect(() => {
    if (existingSession) {
      setSessionId(existingSession.id);
      setSessionCreated(true);
    } else {
      setSessionId(null);
      setSessionCreated(false);
    }
  }, [existingSession]);

  useEffect(() => {
    if (urlDay && urlDay !== selectedDay) {
      setSelectedDay(urlDay);
      setSessionId(null);
      setSessionCreated(false);
    }
  }, [urlDay, selectedDay]);

  // Auto-crée la séance si ?autostart=1 et pas de séance existante
  useEffect(() => {
    if (!isFreeWorkout && autostart && (!urlDay || selectedDay === urlDay) && !sessionCreated && !existingSession && !createSessionMutation.isPending) {
      createSessionMutation.mutate();
    }
    // eslint-disable-next-line
  }, [autostart, isFreeWorkout, urlDay, selectedDay, sessionCreated, existingSession?.id]);

  const createSessionMutation = useMutation({
    mutationFn: () => api.createSession({
      day: isFreeWorkout ? FREE_SESSION_DAY : selectedDay,
      date: sessionDate,
      ...(isFreeWorkout ? { is_free: true } : {}),
    }),
    onSuccess: (data) => {
      playSound('sessionStart');
      setSessionId(data.id);
      setSessionCreated(true);
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e) => setError(e.message),
  });

  const saveSets = async (sets) => {
    const id = sessionId || existingSession?.id;
    if (!id) return;
    await api.saveSets(id, sets);
    queryClient.invalidateQueries({ queryKey: ['session', id] });
    queryClient.invalidateQueries({ queryKey: ['sessions'] });
    queryClient.invalidateQueries({ queryKey: ['lastPerformance'] });
    queryClient.invalidateQueries({ queryKey: ['bests'] });
    queryClient.invalidateQueries({ queryKey: ['achievements'] });
    checkNewBadges();
  };

  const addFreeExercise = async (libraryId) => {
    if (!currentSessionId) return;
    setError(null);
    try {
      await api.addFreeExercise(currentSessionId, libraryId);
      await queryClient.invalidateQueries({ queryKey: ['session', currentSessionId] });
    } catch (err) {
      setError(err.message || "Impossible d'ajouter cet exercice");
      throw err;
    }
  };

  const removeFreeExercise = async (exercise) => {
    const hasData = (sessionSetsByExercise[exercise.id] || []).some((set) =>
      set.weight != null || set.reps != null || set.notes
    );
    if (hasData && !window.confirm(`Retirer « ${exercise.name} » et ses séries de cette séance ?`)) return;
    try {
      await api.removeFreeExercise(currentSessionId, exercise.id);
      await queryClient.invalidateQueries({ queryKey: ['session', currentSessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['lastPerformance'] });
      queryClient.invalidateQueries({ queryKey: ['bests'] });
    } catch (err) {
      setError(err.message || 'Impossible de retirer cet exercice');
    }
  };

  const moveFreeExercise = async (exerciseId, direction) => {
    const from = sessionExercises.findIndex((exercise) => exercise.id === exerciseId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= sessionExercises.length) return;
    const reordered = [...sessionExercises];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    try {
      await api.reorderFreeExercises(currentSessionId, reordered.map((exercise) => exercise.id));
      await queryClient.invalidateQueries({ queryKey: ['session', currentSessionId] });
    } catch (err) {
      setError(err.message || "Impossible de réordonner les exercices");
    }
  };

  const [freePickerOpen, setFreePickerOpen] = useState(false);
  const [newBadges, setNewBadges] = useState([]);
  const checkNewBadges = async () => {
    try {
      const { badges } = await api.getAchievements();
      const unlocked = badges.filter((b) => b.unlocked).map((b) => b.id);
      const raw = localStorage.getItem(SEEN_BADGES_KEY);
      localStorage.setItem(SEEN_BADGES_KEY, JSON.stringify(unlocked));
      const strengthSeeded = localStorage.getItem(SEEN_STRENGTH_KEY) !== null;
      localStorage.setItem(SEEN_STRENGTH_KEY, '1');
      if (raw === null) return;
      const seen = new Set(JSON.parse(raw));
      const fresh = badges.filter((b) => b.unlocked && !seen.has(b.id));
      // Au premier chargement après la mise à jour, les niveaux de force déjà acquis sont enregistrés sans fête
      const freshStrength = strengthSeeded ? fresh.filter((b) => b.strength) : [];
      const freshStandard = fresh.filter((b) => !b.strength);
      // Un seul feu d'artifice par exercice : le niveau le plus haut atteint
      const bestPerLift = new Map();
      freshStrength.forEach((b) => {
        const cur = bestPerLift.get(b.lift_key);
        if (!cur || b.level_index > cur.level_index) bestPerLift.set(b.lift_key, b);
      });
      bestPerLift.forEach((b) => celebrateStrengthLevel(b.title.split(' · ')[0], b.level_name));
      if (freshStandard.length) {
        enqueueCelebration(() => {
          setNewBadges(freshStandard);
          playSound('trophy');
          setTimeout(() => setNewBadges([]), 4300);
        }, 4500);
      }
    } catch (_) {}
  };

  const isWorkoutLoading = isFreeWorkout
    ? !!currentSessionId && isSessionDetailLoading
    : isLoading;

  return (
    <div>
      <h1 className="page-title">Entraînement</h1>
      <p className="page-subtitle">
        Enregistre tes séries et répétitions pour chaque exercice
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 12 }} onClick={toggleSound} aria-pressed={soundOn}>
          {soundOn ? '🔊 Sons' : '🔇 Sons'}
        </button>
      </p>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
            <label className="form-label">{isFreeWorkout ? 'Type de séance' : 'Programme'}</label>
            <select
              className="form-select"
              value={selectedDay}
              onChange={(e) => { setSelectedDay(e.target.value); setSessionDate(todayLocal()); setSessionId(null); setSessionCreated(false); setError(null); }}
            >
              <option value={FREE_WORKOUT_MODE}>Séance libre</option>
              {programNames.map((g) => <option key={g} value={g}>{favoriteNames.has(g) ? '⭐ ' : ''}{g}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-input"
              value={sessionDate}
              onChange={(e) => { setSessionDate(e.target.value); setSessionId(null); setSessionCreated(false); }}
            />
          </div>
          <div>
            {!sessionCreated && !existingSession ? (
              <button
                className="btn btn-primary"
                onClick={() => { requestWakeLock(); unlockAudio(); createSessionMutation.mutate(); }}
                disabled={createSessionMutation.isPending}
              >
                {createSessionMutation.isPending ? '...' : '▶ Démarrer la séance'}
              </button>
            ) : (
              <div className="alert alert-success" style={{ margin: 0, padding: '10px 16px' }}>
                ✓ Séance du {new Date(sessionDate).toLocaleDateString('fr-FR')} — {isFreeWorkout ? FREE_SESSION_DAY : selectedDay}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {newBadges.length > 0 && (
        <div className="badge-toast" onClick={() => setNewBadges([])}>
          {newBadges.map((b) => (
            <div key={b.id}>🎉 Trophée débloqué : {b.icon} <strong>{b.title}</strong></div>
          ))}
        </div>
      )}

      {!isWorkoutLoading && !isFreeWorkout && exercises.length === 0 && (
        <div className="empty-state">Ce programme ne contient aucun exercice. Ajoute-en depuis l'onglet Programmes.</div>
      )}

      {isWorkoutLoading ? (
        <div className="loading"><div className="spinner" />Chargement...</div>
      ) : (
        <div>
          {isFreeWorkout && currentSessionId && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button type="button" className="btn btn-primary" onClick={() => setFreePickerOpen(true)}>
                ＋ Ajouter un exercice
              </button>
            </div>
          )}
          {isFreeWorkout && currentSessionId && visibleExercises.length === 0 && (
            <div className="empty-state">
              <div className="icon">🏋️</div>
              <p>Ajoute un exercice de la bibliothèque pour commencer ta séance.</p>
            </div>
          )}
          {visibleExercises.map((ex) => (
            <ExerciseBlock
              key={ex.id}
              mainExercise={ex}
              sessionId={currentSessionId}
              existingSetsByExercise={sessionSetsByExercise}
              onSave={saveSets}
              onRestStart={(secs) => { requestNotificationPermission(); setRestEndTime(Date.now() + secs * 1000); }}
              onSkip={isFreeWorkout ? undefined : (exerciseId) => skipMutation.mutate({ exerciseId })}
              isFreeWorkout={isFreeWorkout}
              onRemove={isFreeWorkout ? removeFreeExercise : undefined}
              onMove={isFreeWorkout ? moveFreeExercise : undefined}
              canMoveUp={isFreeWorkout && sessionExercises.findIndex((item) => item.id === ex.id) > 0}
              canMoveDown={isFreeWorkout && sessionExercises.findIndex((item) => item.id === ex.id) < sessionExercises.length - 1}
              prevSets={undefined}
              lastPerf={lastPerf}
              bestWeight={bestMap[ex.id] || 0}
            />
          ))}
          {skippedExercises.length > 0 && (
            <div className="skipped-list muted small">
              Retiré de la séance :
              {skippedExercises.map((ex) => (
                <button key={ex.id} type="button" className="btn btn-ghost btn-sm" onClick={() => skipMutation.mutate({ exerciseId: ex.id, restore: true })}>
                  ↩ {ex.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {congrats && (
        <Modal title="🎉 Bravo, séance terminée !" onClose={() => setCongrats(null)}>
          <p style={{ marginTop: 0 }}>Tous les exercices et toutes les séries sont faits. Beau travail, repose-toi bien 💪</p>
          <div className="congrats-stats">
            <div><strong>{congrats.exercises}</strong><span className="muted small">exercices</span></div>
            <div><strong>{congrats.sets}</strong><span className="muted small">séries</span></div>
            <div><strong>{congrats.volume}</strong><span className="muted small">kg soulevés</span></div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={() => setCongrats(null)}>Merci !</button>
        </Modal>
      )}

      {freePickerOpen && (
        <FreeExercisePicker
          existingIds={new Set(sessionExercises.map((exercise) => exercise.library_id))}
          onPick={addFreeExercise}
          onClose={() => setFreePickerOpen(false)}
        />
      )}

      {restEndTime && (
        <RestTimer
          endTime={restEndTime}
          onAdjust={(delta) => setRestEndTime((t) => Math.max(t, Date.now()) + delta * 1000)}
          onDismiss={() => setRestEndTime(null)}
        />
      )}
    </div>
  );
}
